import LocationIcon from '../../assets/icons/LocationIcon';
import classnames from 'classnames';
import useTheme from '../../hooks/ui/useTheme';
import { useTranslation } from 'react-i18next';
import { ComponentPropsWithoutRef, FC, MouseEventHandler, useMemo, useState } from 'react';
import CompanyDetailsModal from '../CompanyDetailsModal';
import { CompanyDistanceInterface } from './CompaniesList';
import useOpeningStatus from '../../hooks/availability/useOpeningStatus';
import HelperMethods from '../../services/exports/HelperMethods';
import AddressHelpers from '../../services/helpers/AddressHelpers';
import { Radio } from '@mui/material';
import { TIME_OPTIONS } from './OrderMethodModal';
import collect from 'collect.js';
import useOrderMethodsStatus from '../../hooks/availability/useOrderMethodsStatus';
import { useLocationData } from './provider/LocationDataProvider';
import RoomServiceForm from './form/RoomServiceForm';
import { ORDER_METHODS } from '../../services/exports/Constants';
import useAppMethods from '../../hooks/utility/useAppMethods';
import moment from 'moment';
import CheckedIcon from '../../modules/mobile/assets/icons/CheckedIcon';

interface Props extends ComponentPropsWithoutRef<'article'> {
  data: CompanyDistanceInterface;
}

interface TimeOption {
  id: TIME_OPTIONS;
  active: boolean;
  title: string;
  description: string;
}

const CompanyCard: FC<Props> = (props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:OrderMethodModal' });
  const { data, className, ...rest } = props;
  const { company, distance } = data;
  const theme = useTheme();
  const { is_open, details_text } = useOpeningStatus({ data: company });
  const { isOrderMethodCurrentlyOpen, isOrderMethodOpenForFutureOrders } = useOrderMethodsStatus(company);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const { company: selectedCompany, setCompany, orderMethod, scheduledFor, setScheduledFor } = useLocationData();
  const isSelected = selectedCompany?.id === company.id;
  const hasAsapOrders = company?.has_asap_orders && isOrderMethodCurrentlyOpen(orderMethod);
  const { toggleOrderMethodModal } = useAppMethods();
  const [selectedTimeOption, setSelectedTimeOption] = useState(() => (scheduledFor ? TIME_OPTIONS.scheduled : null));
  const hasScheduledOrders = company?.has_scheduled_orders || isOrderMethodOpenForFutureOrders(orderMethod);
  const timeOptions = useMemo<TimeOption[]>(
    () =>
      collect()
        .when(hasAsapOrders, (data) =>
          data.push({
            id: TIME_OPTIONS.asap,
            active: selectedTimeOption === TIME_OPTIONS.asap,
            title: t('time_options.asap.title'),
            description: `${HelperMethods.formatTimeInterval(
              orderMethod === ORDER_METHODS.DELIVERY
                ? company?.average_order_delivery_time
                : company?.average_order_preparation_time,
            )} min`,
          }),
        )
        .when(hasScheduledOrders, (data) =>
          data.push({
            id: TIME_OPTIONS.scheduled,
            active: selectedTimeOption === TIME_OPTIONS.scheduled,
            title: company?.scheduled_order_option_title ?? t('time_options.scheduled.title'),
            description: scheduledFor ? moment(scheduledFor).format('ddd HH:mm, MMM DD') : null,
          }),
        )
        .toArray(),
    [hasAsapOrders, hasScheduledOrders, company, selectedTimeOption, scheduledFor, orderMethod],
  );
  const toggleInfoModal = () => setShowInfoModal((prev) => !prev);
  const renderCompanyInfoModal = useMemo(
    () =>
      showInfoModal && <CompanyDetailsModal data={company} openModal={showInfoModal} toggleModal={toggleInfoModal} />,
    [company, showInfoModal],
  );
  const onTimeOptionClick = (timeOption: TIME_OPTIONS) => {
    if (timeOption === TIME_OPTIONS.asap) {
      setScheduledFor(null);
      setSelectedTimeOption(timeOption);
      return;
    }

    toggleOrderMethodModal({ isScheduledModalOpen: true, openModal: true, company: selectedCompany });
  };
  const onClickInfo: MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
    toggleInfoModal();
  };
  const hasRoomServiceForm = orderMethod === ORDER_METHODS.ROOM_SERVICE && company.id === selectedCompany?.id;

  return (
    <article className={classnames('text-black cursor-pointer', className)} {...rest}>
      <div onClick={() => setCompany(company)} className="space-y-[2px]">
        <div className="flex justify-between items-center gap-x-sm">
          <h6>{company.name}</h6>
          {!!distance && (
            <p className="text-sm ml-auto text-brand-primary bg-brand-primary/20 p-1 rounded-[4px] flex-shrink-0">
              {HelperMethods.formatDistance(distance)}
            </p>
          )}
          <Radio checked={isSelected} />
        </div>
        <h6
          className={classnames('text-sm', {
            'text-primary': is_open,
            'text-danger': !is_open,
          })}
        >
          {details_text}
        </h6>
        <div className="flex items-center space-x-1">
          <LocationIcon className="size-4" color={theme.color.gray['700']} />
          <p className="text-sm">
            {AddressHelpers.setCountry(company?.country).format({
              street_name: company?.address,
              city: company?.city,
              zip_code: company?.zip_code,
            })}
          </p>
        </div>
        <div onClick={onClickInfo} className="w-fit">
          <p className="underline text-sm">{t('buttons.see_hours')}</p>
        </div>
      </div>
      {hasRoomServiceForm && <RoomServiceForm className="mt-md" key={company?.id} />}
      {isSelected &&
        timeOptions.map((item) => (
          <article key={`time-option-${item.id}`} className="mt-md">
            <div
              className={classnames('flex flex-row cursor-pointer gap-x-sm p-md border rounded-lg', {
                'bordered !border-black': item.active,
                'hover:border-black': !item.active,
              })}
              onClick={() => onTimeOptionClick(item.id)}
            >
              {(!item.description || item.id === TIME_OPTIONS.asap) && (
                <div className="w-full flex justify-between items-center">
                  <div className="flex items-center gap-x-sm">
                    {item.active && item.id === TIME_OPTIONS.asap && <CheckedIcon color="black" className="stroke-2" />}
                    <p className="text-sm">{item.title}</p>
                  </div>
                  {item.id === TIME_OPTIONS.asap && <p className="text-sm">{item.description}</p>}
                </div>
              )}

              {item.id === TIME_OPTIONS.scheduled && !!item.description && (
                <div className="w-full flex items-center gap-x-md [&>*]:text-sm">
                  <CheckedIcon color="black" className="stroke-2" />
                  <p>{item.description}</p>
                  <p className="ml-auto underline">{t('buttons.change')}</p>
                </div>
              )}
            </div>
          </article>
        ))}
      {renderCompanyInfoModal}
    </article>
  );
};

export default CompanyCard;
